import { DOCUMENT } from '@angular/common';
import { HttpClient, HttpErrorResponse } from '@angular/common/http';
import {
  ErrorHandler,
  Inject,
  Injectable,
  InjectionToken,
  OnDestroy,
  Optional,
  SecurityContext,
  SkipSelf,
} from '@angular/core';
import { DomSanitizer, SafeHtml, SafeResourceUrl } from '@angular/platform-browser';
import { Observable, forkJoin, of as observableOf, throwError as observableThrow } from 'rxjs';
import { catchError, finalize, map, share, tap } from 'rxjs/operators';

export interface SvgOptions {
  viewBox?: string;
}

/**
 * Returns an exception to be thrown in the case when attempting to
 * load an image with a name that cannot be found.
 * @docs-private
 */
export function getSvgImageNameNotFoundError(imageName: string): Error {
  return Error(`Unable to find svg image with the name "${imageName}"`);
}

/**
 * Returns an exception to be thrown when the consumer attempts to use
 * `<campus-svg>` without including @angular/common/http.
 * @docs-private
 */
export function getSvgNoHttpProviderError(): Error {
  return Error(
    'Could not find HttpClient provider for use with Campus Svg images. ' +
      'Please include the HttpClientModule from @angular/common/http in your ' +
      'app imports.'
  );
}

/**
 * Returns an exception to be thrown when a URL couldn't be sanitized.
 * @param url URL that was attempted to be sanitized.
 * @docs-private
 */
export function getSvgFailedToSanitizeUrlError(url: SafeResourceUrl): Error {
  return Error(
    `The URL provided to SvgRegistry was not trusted as a resource URL ` +
      `via Angular's DomSanitizer. Attempted URL was "${url}".`
  );
}

/**
 * Returns an exception to be thrown when a HTML string couldn't be sanitized.
 * @param literal HTML that was attempted to be sanitized.
 * @docs-private
 */
export function getSvgFailedToSanitizeLiteralError(literal: SafeHtml): Error {
  return Error(
    `The literal provided to SvgRegistry was not trusted as safe HTML by ` +
      `Angular's DomSanitizer. Attempted literal was "${literal}".`
  );
}

/**
 * Configuration for a svg image, including the URL and possibly the cached SVG element.
 * @docs-private
 */
class SvgConfig {
  url: SafeResourceUrl | null;
  svgElement: SVGElement | null;

  constructor(data: SafeResourceUrl | SVGElement, public options?: SvgOptions) {
    // Note that we can't use `instanceof SVGElement` here,
    // because it'll break during server-side rendering.
    if (!!(data as any).nodeName) {
      this.svgElement = data as SVGElement;
    } else {
      this.url = data as SafeResourceUrl;
    }
  }
}

/**
 * Service to register and display svgs used by the `<campus-svg>` component.
 * - Registers svg URLs by namespace and name.
 * - Registers svg set URLs by namespace.
 * - Loads svgs from URLs and extracts individual svgs from svg sets.
 */
@Injectable({ providedIn: 'root' })
export class SvgRegistry implements OnDestroy {
  private _document: Document;

  /**
   * URLs and cached SVG elements for individual images. Keys are of the format "[namespace]:[key]".
   */
  public _svgConfigs = new Map<string, SvgConfig>();

  /**
   * SvgConfig objects and cached SVG elements for svg sets, keyed by namespace.
   * Multiple svg sets can be registered under the same namespace.
   */
  private _svgSetConfigs = new Map<string, SvgConfig[]>();

  /** Cache for images loaded by direct URLs. */
  private _cachedSvgsByUrl = new Map<string, SVGElement>();

  /** In-progress svg fetches. Used to coalesce multiple requests to the same URL. */
  private _inProgressUrlFetches = new Map<string, Observable<string>>();

  constructor(
    @Optional() private _httpClient: HttpClient,
    private _sanitizer: DomSanitizer,
    @Optional() @Inject(DOCUMENT) document: any,
    @Optional() private readonly _errorHandler?: ErrorHandler
  ) {
    this._document = document;
  }

  /**
   * Registers an svg by URL in the default namespace.
   * @param svgName Name under which the svg should be registered.
   * @param url
   */
  addSvgImage(svgName: string, url: SafeResourceUrl, options?: SvgOptions): this {
    return this.addSvgImageInNamespace('', svgName, url, options);
  }

  /**
   
   * Registers an image using an HTML string in the default namespace.
   * @param svgName Name under which the svg image should be registered.
   * @param literal SVG source of the icon.
   */
  addSvgImageLiteral(svgName: string, literal: SafeHtml, options?: SvgOptions): this {
    return this.addSvgImageLiteralInNamespace('', svgName, literal, options);
  }

  /**
   * Registers an image by URL in the specified namespace.
   * @param namespace Namespace in which the svg image should be registered.
   * @param svgName Name under which the svg image should be registered.
   * @param url
   */
  addSvgImageInNamespace(namespace: string, svgName: string, url: SafeResourceUrl, options?: SvgOptions): this {
    return this._addSvgConfig(namespace, svgName, new SvgConfig(url, options));
  }

  /**
   * Registers an image using an HTML string in the specified namespace.
   * @param namespace Namespace in which the svg image should be registered.
   * @param svgName Name under which the svg image should be registered.
   * @param literal SVG source of the image.
   */
  addSvgImageLiteralInNamespace(namespace: string, svgName: string, literal: SafeHtml, options?: SvgOptions): this {
    const sanitizedLiteral = this._sanitizer.sanitize(SecurityContext.HTML, literal);

    if (!sanitizedLiteral) {
      throw getSvgFailedToSanitizeLiteralError(literal);
    }

    const svgElement = this._createSvgElementForSingleImage(sanitizedLiteral, options);
    return this._addSvgConfig(namespace, svgName, new SvgConfig(svgElement, options));
  }

  /**
   * Registers an svg set by URL in the default namespace.
   * @param url
   */
  addSvgImageSet(url: SafeResourceUrl, options?: SvgOptions): this {
    return this.addSvgImageSetInNamespace('', url, options);
  }

  /**
   * Registers an svg set using an HTML string in the default namespace.
   * @param literal SVG source of the svg set.
   */
  addSvgImageSetLiteral(literal: SafeHtml, options?: SvgOptions): this {
    return this.addSvgImageSetLiteralInNamespace('', literal, options);
  }

  /**
   * Registers an svg set by URL in the specified namespace.
   * @param namespace Namespace in which to register the svg set.
   * @param url
   */
  addSvgImageSetInNamespace(namespace: string, url: SafeResourceUrl, options?: SvgOptions): this {
    return this._addSvgImageSetConfig(namespace, new SvgConfig(url, options));
  }

  /**
   * Registers an svg set using an HTML string in the specified namespace.
   * @param namespace Namespace in which to register the svg set.
   * @param literal SVG source of the svg set.
   */
  addSvgImageSetLiteralInNamespace(namespace: string, literal: SafeHtml, options?: SvgOptions): this {
    const sanitizedLiteral = this._sanitizer.sanitize(SecurityContext.HTML, literal);

    if (!sanitizedLiteral) {
      throw getSvgFailedToSanitizeLiteralError(literal);
    }

    const svgElement = this._svgElementFromString(sanitizedLiteral);
    return this._addSvgImageSetConfig(namespace, new SvgConfig(svgElement, options));
  }

  /**
   * Returns an Observable that produces the svg image (as an `<svg>` DOM element) from the given URL.
   * The response from the URL may be cached so this will not always cause an HTTP request, but
   * the produced element will always be a new copy of the originally fetched svg image. (That is,
   * it will not contain any modifications made to elements previously returned).
   *
   * @param safeUrl URL from which to fetch the SVG image.
   */
  getSvgImageFromUrl(safeUrl: SafeResourceUrl): Observable<SVGElement> {
    const url = this._sanitizer.sanitize(SecurityContext.RESOURCE_URL, safeUrl);

    if (!url) {
      throw getSvgFailedToSanitizeUrlError(safeUrl);
    }

    const cachedSvgImage = this._cachedSvgsByUrl.get(url);

    if (cachedSvgImage) {
      return observableOf(cloneSvg(cachedSvgImage));
    }

    return this._loadSvgImageFromConfig(new SvgConfig(safeUrl)).pipe(
      tap((svg) => this._cachedSvgsByUrl.set(url, svg)),
      map((svg) => cloneSvg(svg))
    );
  }

  /**
   * Returns an Observable that produces the svg image (as an `<svg>` DOM element) with the given name
   * and namespace. The svg imge must have been previously registered with addSvgImage or addSvgSet;
   * if not, the Observable will throw an error.
   *
   * @param name Name of the svg image to be retrieved.
   * @param namespace Namespace in which to look for the svg image.
   */
  getNamedSvgImage(name: string, namespace: string = ''): Observable<SVGElement> {
    // Return (copy of) cached icon if possible.
    const key = svgKey(namespace, name);
    const config = this._svgConfigs.get(key);

    if (config) {
      return this._getSvgImageFromConfig(config);
    }

    // See if we have any icon sets registered for the namespace.
    const svgSetConfigs = this._svgSetConfigs.get(namespace);

    if (svgSetConfigs) {
      return this._getSvgImageFromSvgSetConfigs(name, svgSetConfigs);
    }

    return observableThrow(getSvgImageNameNotFoundError(key));
  }

  ngOnDestroy() {
    this._svgConfigs.clear();
    this._svgSetConfigs.clear();
    this._cachedSvgsByUrl.clear();
  }

  /**
   * Returns the cached svg image for a SvgConfig if available, or fetches it from its URL if not.
   */
  private _getSvgImageFromConfig(config: SvgConfig): Observable<SVGElement> {
    if (config.svgElement) {
      // We already have the SVG element for this icon, return a copy.
      return observableOf(cloneSvg(config.svgElement));
    } else {
      // Fetch the icon from the config's URL, cache it, and return a copy.
      return this._loadSvgImageFromConfig(config).pipe(
        tap((svg) => (config.svgElement = svg)),
        map((svg) => cloneSvg(svg))
      );
    }
  }

  /**
   * Attempts to find an image with the specified name in any of the SVG image sets.
   * First searches the available cached svg images for a nested element with a matching name, and
   * if found copies the element to a new `<svg>` element. If not found, fetches all svg sets
   * that have not been cached, and searches again after all fetches are completed.
   * The returned Observable produces the SVG element if possible, and throws
   * an error if no svg image with the specified name can be found.
   */
  private _getSvgImageFromSvgSetConfigs(name: string, svgSetConfigs: SvgConfig[]): Observable<SVGElement> {
    // For all the svg set SVG elements we've fetched, see if any contain an svg image with the
    // requested name.
    const namedSvgImage = this._extractSvgImageWithNameFromAnySet(name, svgSetConfigs);

    if (namedSvgImage) {
      // We could cache namedIcon in _SvgConfigs, but since we have to make a copy every
      // time anyway, there's probably not much advantage compared to just always extracting
      // it from the svg set.
      return observableOf(namedSvgImage);
    }

    // Not found in any cached svg sets. If there are svg sets with URLs that we haven't
    // fetched, fetch them now and look for imageName in the results.
    const svgSetFetchRequests: Observable<SVGElement | null>[] = svgSetConfigs
      .filter((svgSetConfig) => !svgSetConfig.svgElement)
      .map((svgSetConfig) => {
        return this._loadSvgSetFromConfig(svgSetConfig).pipe(
          catchError((err: HttpErrorResponse): Observable<SVGElement | null> => {
            const url = this._sanitizer.sanitize(SecurityContext.RESOURCE_URL, svgSetConfig.url);

            // Swallow errors fetching individual URLs so the
            // combined Observable won't necessarily fail.
            const errorMessage = `Loading svg set URL: ${url} failed: ${err.message}`;

            if (this._errorHandler) {
              this._errorHandler.handleError(new Error(errorMessage));
            } else {
              console.error(errorMessage);
            }
            return observableOf(null);
          })
        );
      });

    // Fetch all the svg set URLs. When the requests complete, every SvgSet should have a
    // cached SVG element (unless the request failed), and we can check again for the svg image.
    return forkJoin(svgSetFetchRequests).pipe(
      map(() => {
        const foundSvgImage = this._extractSvgImageWithNameFromAnySet(name, svgSetConfigs);

        if (!foundSvgImage) {
          throw getSvgImageNameNotFoundError(name);
        }

        return foundSvgImage;
      })
    );
  }

  /**
   * Searches the cached SVG elements for the given svg sets for a nested svg image element whose "id"
   * tag matches the specified name. If found, copies the nested element to a new SVG element and
   * returns it. Returns null if no matching element is found.
   */
  private _extractSvgImageWithNameFromAnySet(imageName: string, svgSetConfigs: SvgConfig[]): SVGElement | null {
    // Iterate backwards, so icon sets added later have precedence.
    for (let i = svgSetConfigs.length - 1; i >= 0; i--) {
      const config = svgSetConfigs[i];
      if (config.svgElement) {
        const foundSvgImage = this._extractSvgImageFromSet(config.svgElement, imageName, config.options);
        if (foundSvgImage) {
          return foundSvgImage;
        }
      }
    }
    return null;
  }

  /**
   * Loads the content of the svg image URL specified in the SvgConfig and creates an SVG element
   * from it.
   */
  private _loadSvgImageFromConfig(config: SvgConfig): Observable<SVGElement> {
    return this._fetchUrl(config.url).pipe(
      map((svgText) => this._createSvgElementForSingleImage(svgText, config.options))
    );
  }

  /**
   * Loads the content of the svg image set URL specified in the SvgConfig and creates an SVG element
   * from it.
   */
  private _loadSvgSetFromConfig(config: SvgConfig): Observable<SVGElement> {
    // If the SVG for this svg image set has already been parsed, do nothing.
    if (config.svgElement) {
      return observableOf(config.svgElement);
    }

    return this._fetchUrl(config.url).pipe(
      map((svgText) => {
        // It is possible that the svg image set was parsed and cached by an earlier request, so parsing
        // only needs to occur if the cache is yet unset.
        if (!config.svgElement) {
          config.svgElement = this._svgElementFromString(svgText);
        }

        return config.svgElement;
      })
    );
  }

  /**
   * Creates a DOM element from the given SVG string, and adds default attributes.
   */
  private _createSvgElementForSingleImage(responseText: string, options?: SvgOptions): SVGElement {
    const svg = this._svgElementFromString(responseText);
    this._setSvgAttributes(svg, options);
    return svg;
  }

  /**
   * Searches the cached element of the given SvgConfig for a nested icon element whose "id"
   * tag matches the specified name. If found, copies the nested element to a new SVG element and
   * returns it. Returns null if no matching element is found.
   */
  private _extractSvgImageFromSet(svgSet: SVGElement, imageName: string, options?: SvgOptions): SVGElement | null {
    // Use the `id="imageName"` syntax in order to escape special
    // characters in the ID (versus using the #imageName syntax).
    const svgImageSource = svgSet.querySelector(`[id="${imageName}"]`);

    if (!svgImageSource) {
      return null;
    }

    // Clone the element and remove the ID to prevent multiple elements from being added
    // to the page with the same ID.
    const svgImageElement = svgImageSource.cloneNode(true) as Element;
    svgImageElement.removeAttribute('id');

    // If the icon node is itself an <svg> node, clone and return it directly. If not, set it as
    // the content of a new <svg> node.
    if (svgImageElement.nodeName.toLowerCase() === 'svg') {
      return this._setSvgAttributes(svgImageElement as SVGElement, options);
    }

    // If the node is a <symbol>, it won't be rendered so we have to convert it into <svg>. Note
    // that the same could be achieved by referring to it via <use href="#id">, however the <use>
    // tag is problematic on Firefox, because it needs to include the current page path.
    if (svgImageElement.nodeName.toLowerCase() === 'symbol') {
      return this._setSvgAttributes(this._toSvgElement(svgImageElement), options);
    }

    // createElement('SVG') doesn't work as expected; the DOM ends up with
    // the correct nodes, but the SVG content doesn't render. Instead we
    // have to create an empty SVG node using innerHTML and append its content.
    // Elements created using DOMParser.parseFromString have the same problem.
    // http://stackoverflow.com/questions/23003278/svg-innerhtml-in-firefox-can-not-display
    const svg = this._svgElementFromString('<svg></svg>');
    // Clone the node so we don't remove it from the parent icon set element.
    svg.appendChild(svgImageElement);

    return this._setSvgAttributes(svg, options);
  }

  /**
   * Creates a DOM element from the given SVG string.
   */
  private _svgElementFromString(str: string): SVGElement {
    const div = this._document.createElement('DIV');
    div.innerHTML = str;
    const svg = div.querySelector('svg') as SVGElement;

    if (!svg) {
      throw Error('<svg> tag not found');
    }

    return svg;
  }

  /**
   * Converts an element into an SVG node by cloning all of its children.
   */
  private _toSvgElement(element: Element): SVGElement {
    const svg = this._svgElementFromString('<svg></svg>');
    const attributes = element.attributes;

    // Copy over all the attributes from the `symbol` to the new SVG, except the id.
    for (let i = 0; i < attributes.length; i++) {
      const { name, value } = attributes[i];

      if (name !== 'id') {
        svg.setAttribute(name, value);
      }
    }

    for (let i = 0; i < element.childNodes.length; i++) {
      if (element.childNodes[i].nodeType === this._document.ELEMENT_NODE) {
        svg.appendChild(element.childNodes[i].cloneNode(true));
      }
    }

    return svg;
  }

  /**
   * Sets the default attributes for an SVG element to be used as an icon.
   */
  private _setSvgAttributes(svg: SVGElement, options?: SvgOptions): SVGElement {
    svg.setAttribute('fit', '');
    svg.setAttribute('height', '100%');
    svg.setAttribute('width', '100%');
    svg.setAttribute('focusable', 'false'); // Disable IE11 default behavior to make SVGs focusable.

    if (options && options.viewBox) {
      svg.setAttribute('viewBox', options.viewBox);
    }

    return svg;
  }

  /**
   * Returns an Observable which produces the string contents of the given URL. Results may be
   * cached, so future calls with the same URL may not cause another HTTP request.
   */
  private _fetchUrl(safeUrl: SafeResourceUrl | null): Observable<string> {
    if (!this._httpClient) {
      throw getSvgNoHttpProviderError();
    }

    if (safeUrl == null) {
      throw Error(`Cannot fetch svg image from URL "${safeUrl}".`);
    }

    const url = this._sanitizer.sanitize(SecurityContext.RESOURCE_URL, safeUrl);

    if (!url) {
      throw getSvgFailedToSanitizeUrlError(safeUrl);
    }

    // Store in-progress fetches to avoid sending a duplicate request for a URL when there is
    // already a request in progress for that URL. It's necessary to call share() on the
    // Observable returned by http.get() so that multiple subscribers don't cause multiple XHRs.
    const inProgressFetch = this._inProgressUrlFetches.get(url);

    if (inProgressFetch) {
      return inProgressFetch;
    }

    const req = this._httpClient.get(url, { responseType: 'text' }).pipe(
      finalize(() => {
        this._inProgressUrlFetches.delete(url);
      }),
      share()
    );

    this._inProgressUrlFetches.set(url, req);
    return req;
  }

  /**
   * Registers an icon config by name in the specified namespace.
   * @param namespace Namespace in which to register the icon config.
   * @param imageName Name under which to register the config.
   * @param config Config to be registered.
   */
  private _addSvgConfig(namespace: string, imageName: string, config: SvgConfig): this {
    this._svgConfigs.set(svgKey(namespace, imageName), config);
    return this;
  }

  /**
   * Registers an svg set config in the specified namespace.
   * @param namespace Namespace in which to register the svg config.
   * @param config Config to be registered.
   */
  private _addSvgImageSetConfig(namespace: string, config: SvgConfig): this {
    const configNamespace = this._svgSetConfigs.get(namespace);

    if (configNamespace) {
      configNamespace.push(config);
    } else {
      this._svgSetConfigs.set(namespace, [config]);
    }

    return this;
  }
}

/** @docs-private */
export function SVG_REGISTRY_PROVIDER_FACTORY(
  parentRegistry: SvgRegistry,
  httpClient: HttpClient,
  sanitizer: DomSanitizer,
  document?: any,
  errorHandler?: ErrorHandler
) {
  return parentRegistry || new SvgRegistry(httpClient, sanitizer, document, errorHandler);
}

/** @docs-private */
export const SVG_REGISTRY_PROVIDER = {
  // If there is already an SvgRegistry available, use that. Otherwise, provide a new one.
  provide: SvgRegistry,
  deps: [
    [new Optional(), new SkipSelf(), SvgRegistry],
    [new Optional(), HttpClient],
    DomSanitizer,
    [new Optional(), ErrorHandler],
    [new Optional(), DOCUMENT as InjectionToken<any>],
  ],
  useFactory: SVG_REGISTRY_PROVIDER_FACTORY,
};

/** Clones an SVGElement while preserving type information. */
function cloneSvg(svg: SVGElement): SVGElement {
  return svg.cloneNode(true) as SVGElement;
}

/** Returns the cache key to use for an icon namespace and name. */
function svgKey(namespace: string, name: string) {
  return namespace + ':' + name;
}
